import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DateFormats, Timezones } from 'infrastructure/enums';
import { getUserTimeZone } from 'infrastructure/functions';

import type { TFunction } from 'i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

export type AppointmentMethodVariant = 'phone' | 'sms' | 'email';

export type AppointmentMethodTag = {
  variant: AppointmentMethodVariant;
  label: string;
};

export type AppointmentPatientLike = {
  firstName?: string;
  lastName?: string;
  fullName?: string;
};

export type FormatTimeLabelOptions = {
  todayKey?: string;
  dateKey?: string;
};

const DEFAULTS = {
  unknownPatient: 'Unknown patient',
  timeFormat: 'h:mm A',
  i18n: {
    todayKey: 'homeDashboard.appointments.time.today',
    dateKey: 'homeDashboard.appointments.time.date',
    todayFallback: 'Today, at {{time}}',
    dateFallback: '{{date}}, at {{time}}',
  },
} as const;

const stripTrailingZ = (value: string) => value.replace(/Z$/i, '');
const hasExplicitOffset = (value: string) => /[+-]\d{2}:\d{2}$/.test(value);

const getLabelKeys = (options?: FormatTimeLabelOptions) => ({
  todayKey: options?.todayKey ?? DEFAULTS.i18n.todayKey,
  dateKey: options?.dateKey ?? DEFAULTS.i18n.dateKey,
});

export const mapAppointmentMethodVariant = (
  method?: string,
): AppointmentMethodVariant | undefined => {
  const normalized = method?.trim().toLowerCase();
  if (!normalized) return undefined;

  switch (normalized) {
    case 'phone':
    case 'phone_or_video_conference':
      return 'phone';
    case 'sms':
      return 'sms';
    case 'email':
      return 'email';
    default:
      return undefined;
  }
};

export const buildAppointmentMethodTag = (
  method: string | undefined,
  t: TFunction,
): AppointmentMethodTag | undefined => {
  const variant = mapAppointmentMethodVariant(method);
  if (!variant) return undefined;

  const labelByVariant: Record<AppointmentMethodVariant, string> = {
    phone: t('homeDashboard.appointments.method.phoneVideo'),
    sms: t('homeDashboard.appointments.method.sms'),
    email: t('homeDashboard.appointments.method.email'),
  };

  return { variant, label: labelByVariant[variant] };
};

export const buildPatientName = (patient?: AppointmentPatientLike): string => {
  const fullName = patient?.fullName?.trim();
  if (fullName) return fullName;

  const composed = [patient?.firstName, patient?.lastName]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(' ');

  return composed || DEFAULTS.unknownPatient;
};

export const formatBirthDate = (birthDate?: string): string | undefined => {
  const parsed = birthDate ? dayjs(birthDate) : null;
  if (!parsed?.isValid()) return undefined;
  return parsed.format(DateFormats['MM/DD/YYYY']);
};

const parseBackendTime = (
  raw: string,
  {
    sourceTzWhenMissingOffset = Timezones['America/Chicago'],
    targetTz,
  }: { sourceTzWhenMissingOffset?: string; targetTz?: string } = {},
) => {
  const hasUtcZ = /Z$/i.test(raw);
  const normalized = stripTrailingZ(raw);

  let base: dayjs.Dayjs;
  if (hasUtcZ) {
    base = dayjs.utc(normalized);
  } else if (hasExplicitOffset(normalized)) {
    base = dayjs(normalized);
  } else {
    base = dayjs.tz(normalized, sourceTzWhenMissingOffset);
  }

  if (!base.isValid()) {
    return base;
  }

  if (!targetTz) {
    return base;
  }

  if (typeof targetTz !== 'string' || !targetTz.trim()) {
    return base;
  }

  return base.tz(targetTz);
};

const formatAppointmentLabel = (
  parsedTime: dayjs.Dayjs,
  t: TFunction,
  options?: FormatTimeLabelOptions,
) => {
  if (!parsedTime.isValid()) return '';

  const { todayKey, dateKey } = getLabelKeys(options);
  const timeValue = parsedTime.format(DEFAULTS.timeFormat);

  if (parsedTime.isSame(dayjs(), 'day')) {
    return t(todayKey, DEFAULTS.i18n.todayFallback, { time: timeValue });
  }

  return t(dateKey, DEFAULTS.i18n.dateFallback, {
    date: parsedTime.format(DateFormats['MMM D']),
    time: timeValue,
  });
};

export const formatAppointmentTimeLabel = (
  appointmentTime: string | undefined,
  t: TFunction,
  options?: FormatTimeLabelOptions,
) => {
  if (!appointmentTime) return '';

  const localTz = getUserTimeZone();
  const parsed = parseBackendTime(appointmentTime, { targetTz: localTz });

  return formatAppointmentLabel(parsed, t, options);
};

export const formatAppointmentTimeCSTLabel = (
  appointmentTime: string | undefined,
  t: TFunction,
  options?: FormatTimeLabelOptions,
) => {
  if (!appointmentTime) return '';

  const parsed = parseBackendTime(appointmentTime, {
    targetTz: Timezones['America/Chicago'],
  });

  return formatAppointmentLabel(parsed, t, options);
};

export const toLocalAppointmentDateTime = (
  appointmentTime: string | undefined,
): string | undefined => {
  if (!appointmentTime) return undefined;

  const localTz = getUserTimeZone();
  const parsed = parseBackendTime(appointmentTime, { targetTz: localTz });

  return parsed.isValid() ? parsed.format() : undefined;
};
